import { discordAuthErrorCodeSchema, type DiscordAuthErrorCode } from '@warplan/shared';
import { ApiError } from './api-client.js';

type AuthLanguage = 'fr' | 'en';

export function getAuthLanguage(): AuthLanguage {
  return navigator.language.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

const discordMessages: Record<AuthLanguage, Record<DiscordAuthErrorCode, string>> = {
  fr: {
    DISCORD_ACCOUNT_UNVERIFIED:
      'Vérifie ton adresse mail dans Discord, puis réessaie de te connecter.',
    DISCORD_MEMBERSHIP_PENDING:
      'Termine les étapes d’adhésion au serveur Discord, puis réessaie.',
    DISCORD_ACCESS_DENIED: 'Connexion Discord refusée. Réessaie et autorise la connexion dans Discord.',
    DISCORD_OAUTH_FAILED: 'Connexion Discord impossible. Si Discord te demande de vérifier ton compte, termine cette vérification puis réessaie.',
    OAUTH_STATE_INVALID: 'La session de connexion Discord est invalide ou expirée. Réessaie.',
    NOT_IN_GUILD: 'Rejoins le serveur Discord requis, puis réessaie.',
    ROLE_NOT_ALLOWED: 'Ton compte Discord ne dispose pas du rôle requis pour accéder à cette application.',
    DISCORD_ACCOUNT_BANNED: 'Ce compte est banni de cette application.',
  },
  en: {
    DISCORD_ACCOUNT_UNVERIFIED: 'Verify your email address in Discord, then try signing in again.',
    DISCORD_MEMBERSHIP_PENDING: 'Complete the Discord server membership steps, then try again.',
    DISCORD_ACCESS_DENIED: 'Discord sign-in was denied. Try again and authorize sign-in in Discord.',
    DISCORD_OAUTH_FAILED: 'Unable to sign in with Discord. If Discord asks you to verify your account, complete that verification and try again.',
    OAUTH_STATE_INVALID: 'The Discord sign-in session is invalid or expired. Please try again.',
    NOT_IN_GUILD: 'Join the required Discord server, then try again.',
    ROLE_NOT_ALLOWED: 'Your Discord account does not have the required role to access this application.',
    DISCORD_ACCOUNT_BANNED: 'This account is banned from this application.',
  },
};

export function getDiscordAuthMessage(code: string | null): string | null {
  if (code === null) return null;
  const language = getAuthLanguage();
  const parsed = discordAuthErrorCodeSchema.safeParse(code);
  if (parsed.success) return discordMessages[language][parsed.data];
  return language === 'fr'
    ? 'Connexion Discord impossible. Si Discord te demande de vérifier ton compte, termine cette vérification puis réessaie.'
    : 'Unable to sign in with Discord. If Discord asks you to verify your account, complete that verification and try again.';
}

export function getLocalAuthMessage(error: unknown, operation: 'login' | 'register'): string {
  const language = getAuthLanguage();
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'DISCORD_LOGIN_REQUIRED':
        return language === 'fr'
          ? 'Ce compte utilise Discord. Connecte-toi avec le bouton Discord.'
          : 'This account uses Discord. Sign in with the Discord button.';
      case 'LOCAL_REGISTRATION_DISABLED':
        return language === 'fr'
          ? 'Les inscriptions locales sont désactivées. Connecte-toi avec Discord.'
          : 'Local registration is disabled. Sign in with Discord.';
      case 'INVALID_CREDENTIALS':
      case 'UNAUTHORIZED':
        return language === 'fr' ? 'Identifiant ou mot de passe incorrect.' : 'Invalid login or password.';
      case 'ACCOUNT_BANNED':
      case 'FORBIDDEN':
        return language === 'fr'
          ? 'Ce compte est banni de cette application.'
          : 'This account is banned from this application.';
    }
  }
  if (operation === 'register') {
    return language === 'fr'
      ? 'Inscription impossible. Réessaie.'
      : 'Unable to register. Please try again.';
  }
  return language === 'fr'
    ? 'Connexion impossible. Réessaie.'
    : 'Unable to sign in. Please try again.';
}